import React, { useState, useEffect, useCallback } from "react";
import { downloadRowsAsXlsx } from "../utils/excelDownload";

const API = process.env.REACT_APP_API_URL || "http://localhost:4001/api";

//  Date helpers 
function todayStr() { return new Date().toISOString().split("T")[0]; }
function daysAgo(d) {
  const dt = new Date(); dt.setDate(dt.getDate() - d);
  return dt.toISOString().split("T")[0];
}
function monthStartStr(offset = 0) {
  const dt = new Date();
  dt.setMonth(dt.getMonth() - offset, 1);
  return dt.toISOString().split("T")[0];
}
function monthEndStr(offset = 0) {
  const dt = new Date();
  dt.setMonth(dt.getMonth() - offset + 1, 0);
  return dt.toISOString().split("T")[0];
}
function quarterStart(offset = 0) {
  const dt = new Date();
  const q = Math.floor(dt.getMonth() / 3) - offset;
  const year = dt.getFullYear() + Math.floor(q / 4);
  const qMod = ((q % 4) + 4) % 4;
  return new Date(year, qMod * 3, 1).toISOString().split("T")[0];
}
function quarterEnd(offset = 0) {
  const dt = new Date();
  const q = Math.floor(dt.getMonth() / 3) - offset;
  const year = dt.getFullYear() + Math.floor(q / 4);
  const qMod = ((q % 4) + 4) % 4;
  return new Date(year, qMod * 3 + 3, 0).toISOString().split("T")[0];
}

const DATE_PRESETS = [
  { label: "This Month",    from: () => monthStartStr(0), to: () => monthEndStr(0)  },
  { label: "Last Month",    from: () => monthStartStr(1), to: () => monthEndStr(1)  },
  { label: "This Quarter",  from: () => quarterStart(0),  to: () => quarterEnd(0)   },
  { label: "Last Quarter",  from: () => quarterStart(1),  to: () => quarterEnd(1)   },
  { label: "Last 30 Days",  from: () => daysAgo(30),      to: todayStr              },
  { label: "Last 90 Days",  from: () => daysAgo(90),      to: todayStr              },
  { label: "Custom",        from: null,                    to: null                  },
];

const REPORT_TYPES = [
  { key: "visits",    label: "Visits",    icon: "", desc: "Daily field visit records" },
  { key: "field_days", label: "Field Days", icon: "", desc: "Login & log-off GPS, route km, institution visits" },
  { key: "leads",     label: "Leads",     icon: "", desc: "Pipeline lead status & milestones" },
  { key: "followups", label: "Follow-ups",icon: "", desc: "Follow-up schedules & outcomes" },
  { key: "pipeline",  label: "Pipeline",  icon: "", desc: "Full pipeline stage audit" },
];

// Column definitions per report type
const COLUMNS = {
  visits: [
    { key: "date",             label: "Date" },
    { key: "employee",         label: "Employee" },
    { key: "company",          label: "Company" },
    { key: "institution_name", label: "Institution" },
    { key: "institution_type", label: "Type" },
    { key: "city",             label: "City" },
    { key: "contact_name",     label: "Contact" },
    { key: "contact_phone",    label: "Phone" },
    { key: "lead_status",      label: "Temperature" },
    { key: "interested_in",    label: "Product Interest" },
    { key: "action_required",  label: "Action Required" },
    { key: "comments",         label: "Comments" },
  ],
  field_days: [
    { key: "date",               label: "Date" },
    { key: "employee",           label: "Employee" },
    { key: "company",            label: "Company" },
    { key: "plan_type",          label: "Work Type" },
    { key: "check_in_time",      label: "Login" },
    { key: "check_out_time",     label: "Log-off" },
    { key: "login_latitude",     label: "Login Lat" },
    { key: "login_longitude",    label: "Login Lng" },
    { key: "logoff_latitude",    label: "Log-off Lat" },
    { key: "logoff_longitude",   label: "Log-off Lng" },
    { key: "logoff_source",      label: "Log-off Fix" },
    { key: "route_km",           label: "Route KM" },
    { key: "institution_visits", label: "Institution Visits" },
  ],
  leads: [
    { key: "created_date",         label: "Created Date" },
    { key: "employee",             label: "Employee" },
    { key: "company",              label: "Company" },
    { key: "institution_name",     label: "Institution" },
    { key: "lead_status",          label: "Temperature" },
    { key: "pipeline_stage",       label: "Pipeline Stage" },
    { key: "deal_value",           label: "Deal Value" },
    { key: "proposal_sent_at",     label: "Proposal Sent" },
    { key: "mou_shared_at",        label: "MOU Shared" },
    { key: "mou_signed_at",        label: "MOU Signed" },
    { key: "payment_requested_at", label: "Payment Requested" },
    { key: "payment_received_at",  label: "Payment Received" },
    { key: "next_follow_up",       label: "Next Follow-up" },
    { key: "notes",                label: "Notes" },
  ],
  followups: [
    { key: "scheduled_date",   label: "Scheduled Date" },
    { key: "actual_date",      label: "Actual Date" },
    { key: "employee",         label: "Employee" },
    { key: "company",          label: "Company" },
    { key: "institution_name", label: "Institution" },
    { key: "status",           label: "Status" },
    { key: "action_required",  label: "Action Required" },
    { key: "interested_in",    label: "Product Interest" },
    { key: "feedback",         label: "Feedback" },
    { key: "missed_reason",    label: "Missed Reason" },
    { key: "next_follow_up_date", label: "Next Follow-up Date" },
  ],
  pipeline: [
    { key: "employee",             label: "Employee" },
    { key: "company",              label: "Company" },
    { key: "institution_name",     label: "Institution" },
    { key: "lead_status",          label: "Temperature" },
    { key: "pipeline_stage",       label: "Stage" },
    { key: "deal_value",           label: "Deal Value" },
    { key: "proposal_sent_at",     label: "Proposal Sent" },
    { key: "mou_shared_at",        label: "MOU Shared" },
    { key: "mou_signed_at",        label: "MOU Signed" },
    { key: "payment_requested_at", label: "Payment Requested" },
    { key: "payment_received_at",  label: "Payment Received" },
    { key: "closed_reason",        label: "Close Reason" },
    { key: "created_date",         label: "Created Date" },
  ],
};

//  Badge styles 
const TEMP_BADGE = {
  hot:  { bg: "#fee2e2", color: "#dc2626" },
  warm: { bg: "#fef3c7", color: "#92400e" },
  cold: { bg: "#dbeafe", color: "#1d4ed8" },
};
const STAGE_BADGE = {
  visited:           { bg: "#f1f5f9", color: "#64748b" },
  interested:        { bg: "#dbeafe", color: "#1d4ed8" },
  proposal_sent:     { bg: "#ffedd5", color: "#c2410c" },
  mou_shared:        { bg: "#ede9fe", color: "#7c3aed" },
  mou_signed:        { bg: "#e0e7ff", color: "#4f46e5" },
  payment_requested: { bg: "#fef3c7", color: "#b45309" },
  closed_won:        { bg: "#dcfce7", color: "#15803d" },
  closed_lost:       { bg: "#fee2e2", color: "#dc2626" },
};
const STATUS_BADGE = {
  completed: { bg: "#dcfce7", color: "#15803d" },
  missed:    { bg: "#fee2e2", color: "#dc2626" },
  scheduled: { bg: "#dbeafe", color: "#1d4ed8" },
};

function Badge({ text, bg, color }) {
  if (!text) return <span style={{ color: "#94a3b8", fontSize: 11 }}></span>;
  return (
    <span style={{ background: bg, color, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {text.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  );
}

function CellValue({ colKey, value }) {
  if (!value && value !== 0) return <span style={{ color: "#cbd5e1", fontSize: 11 }}></span>;
  if (colKey === "lead_status") {
    const s = TEMP_BADGE[value?.toLowerCase()] || {};
    return <Badge text={value} bg={s.bg || "#f1f5f9"} color={s.color || "#64748b"} />;
  }
  if (colKey === "pipeline_stage") {
    const s = STAGE_BADGE[value] || {};
    return <Badge text={value} bg={s.bg || "#f1f5f9"} color={s.color || "#64748b"} />;
  }
  if (colKey === "status") {
    const s = STATUS_BADGE[value?.toLowerCase()] || {};
    return <Badge text={value} bg={s.bg || "#f1f5f9"} color={s.color || "#64748b"} />;
  }
  if (colKey === "deal_value") {
    const n = parseFloat(value);
    if (!n) return <span style={{ color: "#cbd5e1", fontSize: 11 }}></span>;
    return <span style={{ fontSize: 12, fontWeight: 600, color: "#16a34a" }}>{n.toLocaleString("en-IN")}</span>;
  }
  if (colKey === "company") {
    const clr = value?.toLowerCase() === "ebekmed" ? "#2AB5A5" : "#E31E24";
    return <span style={{ fontSize: 11, fontWeight: 700, color: clr }}>{value?.toUpperCase()}</span>;
  }
  return <span style={{ fontSize: 12, color: "#1e293b" }}>{value}</span>;
}

// 
// MAIN COMPONENT
// 

export default function ReportsExport({ accent = "#6366f1", userRole, userCompany }) {
  const [reportType, setReportType] = useState("visits");
  const [preset,     setPreset]     = useState(0);
  const [customFrom, setCustomFrom] = useState(daysAgo(30));
  const [customTo,   setCustomTo]   = useState(todayStr());
  const [employees,  setEmployees]  = useState([]);
  const [selEmployee,setSelEmployee]= useState("");
  const [selCompany, setSelCompany] = useState("");
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [meta,       setMeta]       = useState(null);
  const [page,       setPage]       = useState(1);
  const PAGE_SIZE = 50;

  const isCustom = preset === DATE_PRESETS.length - 1;
  const canFilter = ["superadmin","executive","admin"].includes(userRole);
  const canSeeAllCompanies = ["superadmin","executive"].includes(userRole);

  // Load employee list once
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEmployees(Array.isArray(data) ? data.filter(u => u.role === "employee") : []))
      .catch(() => {});
  }, []);

  const getRange = useCallback(() => {
    if (isCustom) return { from: customFrom, to: customTo };
    const p = DATE_PRESETS[preset];
    return { from: p.from(), to: p.to() };
  }, [preset, isCustom, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setPage(1);
    try {
      const { from, to } = getRange();
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ type: reportType, from, to });
      if (selEmployee) params.set("user_id", selEmployee);
      if (canSeeAllCompanies && selCompany) params.set("company", selCompany);
      const res = await fetch(`${API}/reports/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setRows(json.rows || []);
      setMeta({ from: json.from, to: json.to, count: json.count, type: json.type });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getRange, reportType, selEmployee, selCompany, canSeeAllCompanies]);

  // Auto-load on filter change
  useEffect(() => { load(); }, [load]);

  const handleDownload = () => {
    if (!rows.length) return;
    const cols = COLUMNS[reportType] || [];
    const { from, to } = getRange();
    downloadRowsAsXlsx({
      filename: `${reportType}_${from}_${to}.xlsx`,
      sheetName: reportType,
      title: `${REPORT_TYPES.find(item => item.key === reportType)?.label || "CRM"} Report`,
      subtitleRows: [`${from} to ${to}`],
      headers: cols.map(column => column.label),
      rows: rows.map(row => cols.map(column => row[column.key] ?? "")),
    });
  };

  const cols = COLUMNS[reportType] || [];
  const paginated = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(rows.length / PAGE_SIZE);

  return (
    <div style={{ padding: "0 0 24px 0" }}>

      {/*  Report type selector  */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {REPORT_TYPES.map(rt => (
          <button
            key={rt.key}
            onClick={() => setReportType(rt.key)}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              border: "1.5px solid",
              borderColor: reportType === rt.key ? accent : "#e2e8f0",
              background: reportType === rt.key ? accent : "#fff",
              color: reportType === rt.key ? "#fff" : "#475569",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <span>{rt.icon}</span>
            <span>{rt.label}</span>
          </button>
        ))}
      </div>

      {/*  Filter bar  */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "14px 16px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>

        {/* Date presets */}
        <div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>DATE RANGE</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {DATE_PRESETS.map((p, i) => (
              <button
                key={i}
                onClick={() => setPreset(i)}
                style={{
                  padding: "5px 12px", borderRadius: 16, border: "1.5px solid",
                  borderColor: preset === i ? accent : "#e2e8f0",
                  background: preset === i ? accent : "#f8fafc",
                  color: preset === i ? "#fff" : "#64748b",
                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                }}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {/* Custom date pickers */}
        {isCustom && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>FROM</div>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0" }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>TO</div>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ fontSize: 12, padding: "5px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0" }} />
            </div>
          </div>
        )}

        {/* Company filter */}
        {canSeeAllCompanies && (
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>COMPANY</div>
            <select value={selCompany} onChange={e => setSelCompany(e.target.value)}
              style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", minWidth: 120 }}>
              <option value="">All Companies</option>
              <option value="ebek">EBEK</option>
              <option value="ebekmed">EBEKMED</option>
            </select>
          </div>
        )}

        {/* Employee filter */}
        {canFilter && (
          <div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontWeight: 600 }}>EMPLOYEE</div>
            <select value={selEmployee} onChange={e => setSelEmployee(e.target.value)}
              style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: "#fff", minWidth: 160 }}>
              <option value="">All Employees</option>
              {employees
                .filter(e => !selCompany || e.company === selCompany)
                .map(e => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))
              }
            </select>
          </div>
        )}

        {/* Apply + Download */}
        <div style={{ display: "flex", gap: 8, marginLeft: "auto", alignItems: "flex-end" }}>
          <button onClick={load}
            style={{ padding: "7px 18px", borderRadius: 8, background: accent, color: "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
             Apply
          </button>
          <button
            onClick={handleDownload}
            disabled={!rows.length}
            style={{ padding: "7px 18px", borderRadius: 8, background: rows.length ? "#16a34a" : "#e2e8f0", color: rows.length ? "#fff" : "#94a3b8", border: "none", fontSize: 12, fontWeight: 700, cursor: rows.length ? "pointer" : "default" }}>
             Download Excel
          </button>
        </div>
      </div>

      {/*  Meta bar  */}
      {meta && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, fontSize: 12, color: "#64748b", padding: "0 2px" }}>
          <span>
            Showing <strong style={{ color: "#1e293b" }}>{rows.length}</strong> {meta.type} records
            {meta.from && <>  {meta.from} to {meta.to}</>}
          </span>
          {totalPages > 1 && (
            <span>Page {page} of {totalPages}</span>
          )}
        </div>
      )}

      {error && (
        <div style={{ background: "#fee2e2", color: "#dc2626", padding: "10px 16px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>
      )}

      {/*  Table  */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8", fontSize: 14 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}></div>
          Loading report
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8", fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}></div>
          No records found for selected filters.
        </div>
      ) : (
        <>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>#</th>
                    {cols.map(c => (
                      <th key={c.key} style={{ padding: "10px 12px", textAlign: "left", color: "#94a3b8", fontWeight: 700, fontSize: 10, letterSpacing: "0.05em", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                        {c.label.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((row, i) => (
                    <tr
                      key={i}
                      style={{ borderBottom: "1px solid #f8fafc", background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                    >
                      <td style={{ padding: "9px 12px", color: "#94a3b8", fontSize: 11 }}>
                        {(page - 1) * PAGE_SIZE + i + 1}
                      </td>
                      {cols.map(c => (
                        <td key={c.key} style={{ padding: "9px 12px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: c.key === "comments" || c.key === "notes" || c.key === "feedback" ? "normal" : "nowrap" }}>
                          <CellValue colKey={c.key} value={row[c.key]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/*  Pagination  */}
          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: page === 1 ? "#f8fafc" : "#fff", color: page === 1 ? "#cbd5e1" : "#475569", fontSize: 12, fontWeight: 600, cursor: page === 1 ? "default" : "pointer" }}>
                 Prev
              </button>
              {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 7) p = i + 1;
                else if (page <= 4) p = i + 1;
                else if (page >= totalPages - 3) p = totalPages - 6 + i;
                else p = page - 3 + i;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid", borderColor: page === p ? accent : "#e2e8f0", background: page === p ? accent : "#fff", color: page === p ? "#fff" : "#475569", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", background: page === totalPages ? "#f8fafc" : "#fff", color: page === totalPages ? "#cbd5e1" : "#475569", fontSize: 12, fontWeight: 600, cursor: page === totalPages ? "default" : "pointer" }}>
                Next 
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
